import React, { FC } from "react";
import { Alert, Box, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import type { ProcessedAnalysisData } from "../../hooks";
import { usePaygapReportGenerator } from "../../hooks";
import { getRegressionParametersByLang, getRegressionNoteLabel } from "../../../PaygapUploadData/hooks/regressionOptions";

import { useGetPaygapSettings } from "@api/hooks/paygap";
import { useUserStore } from "@store/user";
import { Button, Card, CardContent } from "@components/base";

interface PaygapAnalysisOverviewProps {
  analysisSummary: ProcessedAnalysisData["analysisSummary"];
  processedData?: ProcessedAnalysisData;
}

const PaygapAnalysisOverview: FC<PaygapAnalysisOverviewProps> = ({
  analysisSummary,
  processedData,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useUserStore();
  const { settings } = useGetPaygapSettings(user, true);
  const { generateReport, isGenerating, progress } = usePaygapReportGenerator();

  // Get the regression option from saved settings (default to "1")
  const regressionValue =
    (settings?.analysisParameters as { regression?: string } | undefined)
      ?.regression || "1";
  const regressionLabel = getRegressionParametersByLang(
    String(regressionValue),
    i18n.language,
  );

  const downloadLabel = t("paygap.analysis.overview.downloadReport", "Download Report");
  const generatingLabel = t("paygap.analysis.overview.generating", "Generating...");

  const handleDownloadReport = async (): Promise<void> => {
    if (processedData) {
      await generateReport(processedData);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {t("paygap.analysis.overview.title")}
          </Typography>

          {/* Download Report Button */}
          {processedData && (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isGenerating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FeatherIcon icon="download" size={16} />
                )
              }
              onClick={handleDownloadReport}
              disabled={isGenerating}
            >
              {isGenerating
                ? `${generatingLabel} ${progress}%`
                : downloadLabel}
            </Button>
          )}
        </Box>

        {/* Summary Section */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="subtitle2">
                {t("paygap.analysis.overview.summary")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2">
                  {analysisSummary.employeeRecords}{" "}
                  {t("paygap.analysis.overview.employeeRecords")}
                </Typography>
                <Typography variant="body2">
                  {t("paygap.analysis.overview.analysisDate")}{" "}
                  {analysisSummary.analysisDate}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">
                  {t("paygap.analysis.overview.statisticalResult")}
                </Typography>
                <Chip
                  label={
                    analysisSummary.gapDetected
                      ? t("paygap.analysis.overview.gapDetected")
                      : t("paygap.analysis.overview.noGap")
                  }
                  color={analysisSummary.gapDetected ? "error" : "success"}
                  size="small"
                />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">
                  {t("paygap.analysis.overview.complianceRisk")}
                </Typography>
                <Chip
                  label={
                    analysisSummary.complianceRisk === "High"
                      ? t("paygap.analysis.overview.highRisk")
                      : analysisSummary.complianceRisk === "Medium"
                        ? t("paygap.analysis.overview.mediumRisk")
                        : t("paygap.analysis.overview.noRisk")
                  }
                  color={
                    analysisSummary.complianceRisk === "High"
                      ? "error"
                      : analysisSummary.complianceRisk === "Medium"
                        ? "warning"
                        : "success"
                  }
                  size="small"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Regression Parameters Note */}
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>
              {t(
                "paygap.analysis.overview.regressionParametersLabel",
                getRegressionNoteLabel(i18n.language),
              )}
            </strong>{" "}
            {regressionLabel}
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default PaygapAnalysisOverview;
